import { useQuery } from '@tanstack/react-query';
import { getAppFeatures } from 'shared/api/app-features';
import { AppFeaturesSchema } from 'contracts/app-features';
import { STORAGE_MODULE_FALLBACK, type StorageModuleState } from 'domain/storage/storage-module';

/**
 * NEX-1645 (plan 2026-10-20, Tarea 3.3) — the app-wide switches (`GET /app/features`) as a react-query hook.
 * The backoffice flips them rarely, so five minutes of cache is enough; every reader (the layout, the
 * wizard, the storage routes) shares one cache entry.
 */
export const appFeaturesKeys = {
  all: ['app-features'] as const
};

export interface AppFeaturesState {
  /** The storage module's state; `'off'` while the first answer loads or when the call failed (the safe side). */
  storage: StorageModuleState;
  /** True until the first answer (or the final failure) arrives — for screens that must wait instead of hiding. */
  isLoading: boolean;
}

export const useAppFeatures = (): AppFeaturesState => {
  const query = useQuery({
    queryKey: appFeaturesKeys.all,
    queryFn: () => getAppFeatures().then((res) => AppFeaturesSchema.parse((res.data as { data?: unknown } | undefined)?.data)),
    staleTime: 5 * 60 * 1000
  });
  // A later refetch that fails keeps the last state it read (react-query keeps `data`): a passing network
  // error does not take «Depósitos» away mid-session. Only "never read" falls back.
  return { storage: query.data?.storage ?? STORAGE_MODULE_FALLBACK, isLoading: query.isPending };
};
